import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Star, Building2, IndianRupee, Edit3, UtensilsCrossed, Trash2, Package } from "lucide-react";

export default function VendorDashboard() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState({});

  const load = async () => {
    try { const { data } = await api.get("/vendors/me/dashboard"); setData(data); setDraft({ description: data.vendor.description || "", services: data.vendor.services || [], active: data.vendor.active }); }
    catch (e) { /* not vendor */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (user) load(); }, [user]);

  const save = async () => {
    try {
      await api.put("/vendors/me", draft);
      toast.success("Updated");
      setEdit(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  if (loading || !data) return <div className="panel-shell"><div className="max-w-7xl mx-auto px-5 py-20 text-white/60">Loading...</div></div>;
  if (!user || user.role !== "vendor") {
    return (
      <div className="panel-shell">
        <div className="max-w-3xl mx-auto px-5 py-20 text-center" data-testid="vendor-dash-no-access">
          <h1 className="font-display font-black text-4xl text-white">Vendor only</h1>
          <Link to="/login" className="brut-btn mt-6 inline-flex">Sign in</Link>
        </div>
      </div>
    );
  }
  const v = data.vendor;
  const isFoodVendor = ["food", "tiffin"].includes(v.category);
  const activeSubs = data.subscriptions.filter(s => s.status === "active");
  const revenue = activeSubs.reduce((s, x) => s + (x.monthly_fee || 0), 0);

  return (
    <div className="panel-shell">
    <div className="max-w-7xl mx-auto px-5 md:px-10 py-10" data-testid="vendor-dashboard">
      <div className="brut-card p-6 bg-[#ECFAF1]">
        <span className="brut-tag lime">{v.category}</span>
        <h1 className="font-display font-black text-3xl md:text-4xl mt-2">{v.business_name}</h1>
        <p className="text-[#484848] mt-1">{v.city}{v.pincode ? ` · ${v.pincode}` : ""}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className="brut-card p-4"><div className="brut-tag lime">Subscriptions</div><div className="font-display font-black text-4xl mt-2">{activeSubs.length}</div></div>
        <div className="brut-card p-4"><div className="brut-tag pink">Reviews</div><div className="font-display font-black text-4xl mt-2">{data.reviews.length}</div></div>
        <div className="brut-card p-4"><div className="brut-tag blue">Rating</div><div className="font-display font-black text-4xl mt-2 flex items-center gap-1"><Star size={20} className="fill-[#FFC857]"/> {v.rating || 0}</div></div>
        <div className="brut-card p-4 bg-[#2ED573]"><div className="brut-tag white">MRR (₹)</div><div className="font-display font-black text-3xl mt-2">{revenue.toLocaleString()}</div></div>
      </div>

      {isFoodVendor && <div className="mt-6"><MenuManager vendorId={v.id}/></div>}

      <div className="mt-6"><Link to="/orders" className="brut-btn white !text-xs"><Package size={14}/> View food orders</Link></div>

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <div className="brut-card p-5">
          <div className="flex justify-between items-center mb-3">
            <h2 className="font-display font-black text-2xl">Profile</h2>
            <button className="brut-btn white !py-1 !px-3 !text-xs" onClick={()=>setEdit(!edit)} data-testid="vendor-edit"><Edit3 size={12}/> {edit ? "Cancel" : "Edit"}</button>
          </div>
          {!edit ? (
            <div className="space-y-2 text-sm">
              <div><strong>Status:</strong> <span className={`brut-tag ${v.active ? "lime" : "pink"} !text-[10px]`}>{v.active ? "active" : "paused"}</span></div>
              <div><strong>About:</strong><p className="text-[#484848] mt-1">{v.description || "—"}</p></div>
              <div><strong>Services:</strong>
                <ul className="mt-1 space-y-1">
                  {(v.services || []).map((s, i) => <li key={i} className="text-[#484848]">· {s.name} — ₹{s.price}{s.unit ? `/${s.unit}` : ""}</li>)}
                </ul>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={draft.active} onChange={(e)=>setDraft({...draft, active: e.target.checked})}/> Active</label>
              <textarea className="brut-input min-h-[80px]" value={draft.description} onChange={(e)=>setDraft({...draft, description: e.target.value})} data-testid="vendor-desc-edit"/>
              <button className="brut-btn" onClick={save} data-testid="vendor-save">Save</button>
            </div>
          )}
        </div>

        <div>
          <h2 className="font-display font-black text-2xl mb-3">Active subscriptions</h2>
          <div className="space-y-2">
            {activeSubs.length === 0 && <div className="brut-card p-6 text-center text-[#484848]">No PGs subscribed yet.</div>}
            {activeSubs.map(s => (
              <div key={s.id} className="brut-card p-3 text-sm" data-testid={`sub-${s.id}`}>
                <div className="flex justify-between gap-2 flex-wrap">
                  <strong>{s.property_title}</strong>
                  <span className="flex items-center gap-1"><IndianRupee size={12}/>{s.monthly_fee}/mo</span>
                </div>
                <div className="text-xs text-[#484848] mt-1">{s.plan_name} · since {s.started_at?.slice(0, 10)}</div>
              </div>
            ))}
          </div>

          <h2 className="font-display font-black text-2xl mt-6 mb-3">Recent reviews</h2>
          <div className="space-y-2">
            {data.reviews.slice(0, 5).map(r => (
              <div key={r.id} className="brut-card p-3 text-sm">
                <div className="flex justify-between"><strong>{r.user_name}</strong><span><Star size={12} className="inline fill-[#FFC857]"/> {r.rating}</span></div>
                <p className="text-xs text-[#484848] mt-1">{r.comment}</p>
              </div>
            ))}
            {data.reviews.length === 0 && <div className="brut-card p-6 text-center text-[#484848]">No reviews yet.</div>}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}

function MenuManager({ vendorId }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", price: "", category: "lunch", veg: true, available: true, prep_time_mins: 30 });
  const [editId, setEditId] = useState(null);
  const reload = () => api.get(`/food/menu/${vendorId}`).then(r => setItems(r.data)).catch(()=>{});
  useEffect(() => { reload(); }, [vendorId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) return;
    try {
      const payload = { ...form, price: parseFloat(form.price), prep_time_mins: parseInt(form.prep_time_mins) };
      if (editId) await api.put(`/food/menu/${editId}`, payload);
      else await api.post("/food/menu", payload);
      toast.success(editId ? "Updated" : "Added");
      setForm({ name: "", description: "", price: "", category: "lunch", veg: true, available: true, prep_time_mins: 30 }); setEditId(null);
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const startEdit = (it) => { setEditId(it.id); setForm({ name: it.name, description: it.description, price: it.price, category: it.category, veg: it.veg, available: it.available, prep_time_mins: it.prep_time_mins }); };
  const del = async (id) => {
    if (!window.confirm("Remove this item?")) return;
    try { await api.delete(`/food/menu/${id}`); toast.success("Removed"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="brut-card p-5" data-testid="menu-manager">
      <h2 className="font-display font-black text-2xl flex items-center gap-2 mb-4"><UtensilsCrossed size={20}/> Menu ({items.length})</h2>
      <form onSubmit={submit} className="grid md:grid-cols-3 gap-2 mb-5">
        <input className="brut-input md:col-span-2" placeholder="Dish name" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="m-name"/>
        <input className="brut-input" type="number" placeholder="Price ₹" value={form.price} onChange={(e)=>setForm({...form, price: e.target.value})} data-testid="m-price"/>
        <input className="brut-input md:col-span-2" placeholder="Description (optional)" value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})}/>
        <select className="brut-input" value={form.category} onChange={(e)=>setForm({...form, category: e.target.value})}>
          {["breakfast", "lunch", "dinner", "snack", "beverage", "combo"].map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.veg} onChange={(e)=>setForm({...form, veg: e.target.checked})}/> Veg</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.available} onChange={(e)=>setForm({...form, available: e.target.checked})}/> Available</label>
        <button className="brut-btn" type="submit" data-testid="m-submit">{editId ? "Update" : "+ Add item"}</button>
      </form>
      <div className="space-y-2">
        {items.map(it => (
          <div key={it.id} className="brut-border p-3 flex justify-between items-center text-sm" data-testid={`m-item-${it.id}`}>
            <div>
              <div className="flex items-center gap-2"><span className={`w-2.5 h-2.5 border border-[#0E1F18] ${it.veg ? "bg-[#25A163]" : "bg-[#FF7E68]"}`}/><strong>{it.name}</strong> <span className="text-xs text-[#5A5A5A]">· {it.category}</span></div>
              {it.description && <div className="text-xs text-[#5A5A5A] mt-1">{it.description}</div>}
            </div>
            <div className="flex items-center gap-2"><strong>₹{it.price}</strong>
              <button className="brut-btn white !p-2 !text-xs" onClick={()=>startEdit(it)} aria-label="edit"><Edit3 size={12}/></button>
              <button className="brut-btn pink !p-2 !text-xs" onClick={()=>del(it.id)} aria-label="delete"><Trash2 size={12}/></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-center text-[#5A5A5A] py-4">No menu items yet. Add your first dish above.</div>}
      </div>
    </div>
  );
}
